import { dbCols } from '@cc/schema';
import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Table } from 'antd';
import { useGetLocale, useTranslate, type IResourceComponentsProps } from '@refinedev/core';
import { Tooltip } from 'antd';
import { format } from 'date-fns';
import { useWindowSize } from 'rooks';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { WorryTree } from '../../schema/be-graphql-generated';

import { DiagramImage } from '../../components/DiagramImage';
import { generateWorryTreeUmlText } from '@cc/utils';
import { useContext, useEffect } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { useNavigate } from 'react-router-dom';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const WorryTreeList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const { innerWidth } = useWindowSize();

  // @ts-ignore
  const { tableProps, sorters, searchFormProps, filters, setFilters } =
    useTable<WorryTree>({
      // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
      resource: 'worryTrees',
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },

      meta: {
        operation: 'worryTrees',
        fields: [
          {
            data: [
              '_id',
              'userId',
              'createdAt',
              'worryAbout',
              'selectedActivityIdx',
              'isNotSolvable',
              'options',
              'plan',
              'immediateAction',
            ],
          },
          'count',
        ],
      },

      filters: {
        initial: clientFilterId
          ? [
              {
                field: 'userId',
                operator: 'eq',
                value: clientFilterId,
              },
            ]
          : [],
      },

      sorters: {
        initial: [
          {
            field: 'createdAt',
            order: 'desc',
          },
        ],
      },
    });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  // the WHOLE practice roster (P011) — the client column and its filter must name every row's owner
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  const getLocale = useGetLocale();

  useActivityTracking(dbCols.worryTrees, fixedClientId, !fixedClientId);

  const expandedRowRender = (record: WorryTree) => {
    if (!record) {
      return null;
    }

    const umlText = generateWorryTreeUmlText(
      // @ts-ignore
      record,
      // @ts-ignore
      translate,
      getLocale(),
      (innerWidth || 0) < 850,
    );
    return (
      <div
        style={{
          display: 'grid',
          maxWidth: '100%',
        }}
      >
        {umlText ? (
          <DiagramImage
            source={umlText}
            fallbackLines={[
              record.worryAbout,
              record.options,
              record.plan,
              record.immediateAction,
            ].flatMap((text) => (text ?? '').split(/\r?\n/))}
            style={{
              maxWidth: '100%',
              maxHeight: '100vh',
              margin: 'auto',
            }}
          />
        ) : null}
      </div>
    );
  };

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('shared.worryTree.worryTreesTitle')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-worry-tree-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-worry-tree-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/worryTrees/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowRender,
        }}
        data-testid="worry-trees-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: WorryTree) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}
        <Table.Column
          key="worryAbout"
          dataIndex="worryAbout"
          title={translate('shared.worryTrees.worryAbout')}
        />
        <Table.Column<WorryTree>
          key="isNotSolvable"
          dataIndex="isNotSolvable"
          title={translate('shared.worryTrees.notSolvable')}
          render={(value, record) => {
            return translate('resourcesCommon.' + (value ? 'yes' : 'no'));
          }}
        />
        <Table.Column
          key="options"
          dataIndex="options"
          title={translate('shared.worryTrees.options')}
        />
        <Table.Column
          key="immediateAction"
          dataIndex="immediateAction"
          title={translate('shared.worryTrees.immediateAction')}
        />
        <Table.Column
          key="plan"
          dataIndex="plan"
          title={translate('shared.worryTrees.plan')}
        />
        <Table.Column<WorryTree>
          key="selectedActivityIdx"
          dataIndex="selectedActivityIdx"
          title={translate('shared.worryTrees.refocusActivity')}
          render={(value, record) => {
            const options = translate('shared.worryTree.refocusIdeas', {
              returnObjects: true,
            });

            const activity =
              record.selectedActivityIdx === null
                ? ''
                : // @ts-ignore
                  options[record.selectedActivityIdx || 0]?.title;

            return activity;
          }}
        />
        <Table.Column
          key="createdAt"
          dataIndex="createdAt"
          title={translate('resourcesCommon.createdAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss a',
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
